import { CustomPropertiesType, DateString, DateTimeString } from 'types';
import { Lite, PATCHSchema, POSTSchema } from 'types/api';
import { Org, OrgLite } from 'types/org';
import { UserLite } from 'types/user';
import { Tag } from 'types/clientV2/tag';
import { OrgSupplier, PublicSupplier, OrgSupplierLite } from 'types/suppliers';
import { ProductionRunLite } from 'types/production';
import { StockLocationSchemaLite } from 'types/clientV2/stockLocations';
import { OrgPart } from 'types/part';
import { TeamLite } from 'types/properties';
import { SupplierQuoteLineSchemaLite } from 'types/sourcing';
import { OrgPartSchema, PART_VERSION } from 'types/clientV2/parts';
import {
  DimensionClassOptions,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';
import {
  DecoratedOffer,
  KittingEstimateLine,
  OptimizerLineResult,
  OptimizerResult,
} from 'types/offerOptimizer';
import { PurchaseRule } from 'types/inventory';
import { PurchaseRequestStatus } from 'types/procurement';

export enum PurchaseRequestGroupType {
  CART = 'cart',
  RFQ = 'rfq',
  REQUISITION = 'requisition',
}

export enum PurchaseRequestGroupDestinationType {
  SHIP_AS_A_KIT = 'ship_as_a_kit',
  STORE_AT_COFACTR = 'store_at_cofactr',
  DROPSHIP = 'dropship',
}

export type PurchaseRequestGroupOptimizerEstimates = Pick<
  OptimizerResult,
  'leadTime' | 'totalLandedCosts' | 'suppliers'
>;

export type CartOrRfqType =
  | PurchaseRequestGroupType.CART
  | PurchaseRequestGroupType.RFQ;

export type PurchaseRequestGroupSchema = {
  id: string;
  name: string;
  internalNotes: string | null;
  externalNotes: string | null;
  createdAt: DateTimeString;
  archivedAt: DateTimeString | null;
  org: Lite<Org>;
  processingOrg: Lite<Org>;
  requestedBy: UserLite | null;
  archivedBy: UserLite | null;
  tags: Tag[];
  customProperties: CustomPropertiesType;
  archived: boolean;
  approved: boolean;
  voided: boolean;
  purchaseRequestGroupCode: string;
  externalPoNumber: string | null;
  externalPoTotal: number | null;
  destinationLocation: StockLocationSchemaLite | null;
  carrier: string | null;
  service: string | null;
  printedNotes: string | null;
  shared: boolean | null;
  cofactrCreditApprovedAt: DateTimeString | null;
  type: PurchaseRequestGroupType;
  destinationType: PurchaseRequestGroupDestinationType;
  selectedLeadTime: number;
  selectedShipSpeed: number | null;
  approvedTotal: number;
  approvedKittingTotal: number | null;
  approvedOrderTotal: number;
  optimizerEstimates: PurchaseRequestGroupOptimizerEstimates;
  team: TeamLite;
  kitEstimates?: KittingEstimateLine[] | null;
  numLines: number;
  approvedBy: UserLite | null;
  approvedAt: DateTimeString | null;
  orderedBy: UserLite | null;
  orderedAt: DateTimeString | null;
  voidedAt: DateTimeString | null;
  voidedBy: UserLite | null;
  teams: string | null;
  trackingNumbers: string | null;
  status: PurchaseRequestStatus;
  purchaseRule: PurchaseRule['id'] | null;
};

export type PurchaseRequestGroupSchemaLite = Lite<PurchaseRequestGroupSchema>;

export type PurchaseRequestGroupPOSTSchema = POSTSchema<
  PurchaseRequestGroupSchema,
  'name',
  'processingOrg' | 'type' | 'destinationLocation' | 'destinationType'
>;

export type PurchaseRequestGroupPATCHSchema =
  PATCHSchema<PurchaseRequestGroupSchemaLite> & {
    optimizerEstimates?: PurchaseRequestGroupOptimizerEstimates;
    cofactrCreditApproved?: boolean;
  };

export type PurchaseRequestSchema = {
  id: string;
  /** --------------------------------- */
  /** --- Deprecated Part V1 Fields --- */
  /** --------------------------------- */
  part: string;
  orgPart: OrgPart | string;
  /** -------------------------- */
  /** --- New Part V2 Fields --- */
  /** -------------------------- */
  partVersion: PART_VERSION;
  v2OrgPart: OrgPartSchema['id'];
  v2OrgParts: OrgPartSchema[];
  altV2OrgParts: OrgPartSchema['id'][];
  unapprovedAltV2OrgParts: OrgPartSchema['id'][];
  /** ------------------------ */
  /** --- Remaining Fields --- */
  /** ------------------------ */
  description: string;
  quant: number;
  targetLineTotal: number;
  needDeliveryBy: DateString | null;
  requestedAt: DateTimeString | null;
  approvedAt: DateTimeString | null;
  assignedAt: DateTimeString | null;
  archivedAt: DateTimeString | null;
  voidedAt: DateTimeString | null;
  requestedNotes: string | null;
  assignedNotes: string | null;
  approvedNotes: string | null;
  voidedNotes: string | null;
  dropShip: boolean;
  org: OrgLite;
  processingOrg: OrgLite;
  requestedBy: UserLite;
  assignedBy: UserLite;
  archivedBy: UserLite;
  assignedTo: UserLite;
  approvedBy: UserLite;
  voidedBy: UserLite;
  purchaseRule: PurchaseRule | null;
  productionRun: ProductionRunLite;
  orgSupplier: OrgSupplierLite;
  destinationLocation: StockLocationSchemaLite;
  purchaseRequestGroup: PurchaseRequestGroupSchemaLite;
  tags: Tag[];
  customProperties: CustomPropertiesType;
  team: TeamLite;
  supplierQuoteLine: SupplierQuoteLineSchemaLite;
  isRequestToBuy: boolean;
  isRequestToSource: boolean;
  duties: string | null;
  salesTax: string | null;
  shipping: string | null;
  otherTotal: string | null;
  dimensionUnit: UnitOfMeasureOptions;
  dimensionValue: number;
  dimensionClass: DimensionClassOptions;
  status: PurchaseRequestStatus;
  optimizerEstimates?: OptimizerLineResult | null;
  printedNotes?: string;
};

export type PurchaseRequestSchemaLite = Lite<PurchaseRequestSchema>;

export type PurchaseRequestPATCHSchema = PATCHSchema<PurchaseRequestSchema>;

export type SubmitPurchaseRequestForOrderRequest = {
  id: string;
  optimizerEstimates: OptimizerLineResult;
  targetLineTotal: number | null;
  duties?: number | null;
  salesTax?: number | null;
  shipping?: number | null;
  otherTotal?: number | null;
  kbOffer: DecoratedOffer;
  supplierQuoteLine?: string | null;
  orgSupplier?: OrgSupplier['id'] | null;
  publicSupplier?: PublicSupplier['id'] | null;
};

export type SubmitPurchaseRequestGroupsForOrderRequest = {
  destinationLocation?: string;
  carrier?: string;
  service?: string;
  printedNotes?: string;
  optimizerEstimates: PurchaseRequestGroupOptimizerEstimates;
  kitEstimates?: KittingEstimateLine[] | null;
  selectedLeadTime: number;
  selectedShipSpeed?: number | null;
  approvedTotal: number;
  approvedKittingTotal?: number | null;
  approvedOrderTotal: number;
  purchaseRequests: SubmitPurchaseRequestForOrderRequest[];
  approved: boolean;
};
